import { FormContainer, Treturn, useFetch } from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  fileIntegrationSchema,
  TFileIntegrationForm,
} from "./fileIntegrationSchema";
import { BasicData } from "./BasicData";
import {
  createFileIntegration,
  getFileIntegrationById,
  updateFileIntegrationById,
} from "../../../../../services/uBusAutomationRobot";

const translationPath = "page.4uBus.uBusAutomationRobot.";
const FileIntegration = () => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createFileIntegration,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateFileIntegrationById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getFileIntegrationById,
  );

  const saveHandler = (v: TFileIntegrationForm) => {
    return save({
      ...v,
      name: v.name,
      enable: v.enable,
      description: v.description || undefined,
      path: v.path,
      suffixPrefix: v.suffixPrefix || undefined,
      identificationPosition: v.identificationPosition || undefined,
      pathSeparator: v.pathSeparator ? v.pathSeparator : undefined,
      examMachineId: v.examMachine.id,
      _4uBusAutomationId: v.uBusAutomation.id,
      robotRules: [
        {
          rotationModeId: v.rotationMode ? v.rotationMode.id : undefined,
          integrationAttributeId: v.integrationAttribute.id,
          copyProcessId: v.copyProcess ? v.copyProcess.id : undefined,
          renameFileProcessId: v.renameFileProcess
            ? v.renameFileProcess.id
            : undefined,
          _4uBusAutomationExecutionId: v.uBusAutomationExecution.id,
          minutes: v.minutes,
          generatePdfWithImages: v.generatePdfWithImages,
          rotateImages: v.rotateImages,
          physicianWillReport: v.physicianWillReport,
          internalProcedureIntegrationId:
            v.internalProcedureIntegrationId || undefined,
          medicalCertificateContent: v.medicalCertificateContent || undefined,
          insertReportTestPdf: v.insertReportTestPdf,
          insertReportTestPdfAsImage: v.insertReportTestPdfAsImage,
          insertImageAsAttachment: v.insertImageAsAttachment,
          minutesToGroupPdf: v.minutesToGroupPdf,
          makeCopyFilesEndOfProcess: v.makeCopyFilesEndOfProcess,
          copyFilesPath: v.copyFilesPath || undefined,
        },
      ],
    });
  };

  const updateHandler = (v: TFileIntegrationForm, id: string) => {
    return update({
      item: {
        ...v,
        id: v.id,
        name: v.name,
        enable: v.enable,
        description: v.description || undefined,
        path: v.path,
        suffixPrefix: v.suffixPrefix || undefined,
        identificationPosition: v.identificationPosition || undefined,
        pathSeparator: v.pathSeparator ? v.pathSeparator : undefined,
        examMachineId: v.examMachine.id,
        _4uBusAutomationId: v.uBusAutomation.id,
        robotRules: [
          {
            id: v.idRobotRule,
            rotationModeId: v.rotationMode ? v.rotationMode.id : undefined,
            integrationAttributeId: v.integrationAttribute.id,
            copyProcessId: v.copyProcess ? v.copyProcess.id : undefined,
            renameFileProcessId: v.renameFileProcess
              ? v.renameFileProcess.id
              : undefined,
            _4uBusAutomationExecutionId: v.uBusAutomationExecution.id,
            minutes: v.minutes,
            generatePdfWithImages: v.generatePdfWithImages,
            internalProcedureIntegrationId:
              v.internalProcedureIntegrationId || undefined,
            medicalCertificateContent: v.medicalCertificateContent || undefined,
            physicianWillReport: v.physicianWillReport,
            rotateImages: v.rotateImages,
            insertReportTestPdf: v.insertReportTestPdf,
            insertReportTestPdfAsImage: v.insertReportTestPdfAsImage,
            insertImageAsAttachment: v.insertImageAsAttachment,
            minutesToGroupPdf: v.minutesToGroupPdf,
            makeCopyFilesEndOfProcess: v.makeCopyFilesEndOfProcess,
            copyFilesPath: v.copyFilesPath || undefined,
          },
        ],
      },
      id: id,
    });
  };
  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TFileIntegrationForm>> => {
      const { data, success } = await get(id);
      if (data) {
        const firstRule = data.robotRules?.[0];

        const newData: TFileIntegrationForm = {
          ...data,
          id: data.id,
          enable: data.enable,
          suffixPrefix: data.suffixPrefix,
          identificationPosition: data.identificationPosition,
          examMachineModality: {
            id: data.examMachine.examMachineModality.id,
            name: data.examMachine.examMachineModality.name,
            code: data.examMachine.examMachineModality.code
              ? String(data.examMachine.examMachineModality.code)
              : null,
          },
          examMachine: {
            id: data.examMachine.id,
            name: data.examMachine.name,
          },
          uBusAutomation: {
            id: data._4uBusAutomation.id,
            name: data._4uBusAutomation.name,
            code: data._4uBusAutomation.code
              ? String(data._4uBusAutomation.code)
              : null,
          },
          idRobotRule: firstRule.id,
          minutes: firstRule.minutes.toString(),
          generatePdfWithImages: firstRule.generatePdfWithImages,
          rotateImages: firstRule.rotateImages,
          insertReportTestPdf: firstRule.insertReportTestPdf,
          internalProcedureIntegrationId:
            firstRule.internalProcedureIntegrationId,
          medicalCertificateContent: firstRule.medicalCertificateContent,
          physicianWillReport: firstRule.physicianWillReport,
          insertReportTestPdfAsImage: firstRule.insertReportTestPdfAsImage,
          insertImageAsAttachment: firstRule.insertImageAsAttachment,
          makeCopyFilesEndOfProcess: firstRule.makeCopyFilesEndOfProcess,
          minutesToGroupPdf: firstRule.minutesToGroupPdf.toString(),
          copyFilesPath: firstRule.copyFilesPath,
          rotationMode: firstRule.rotationMode
            ? {
                id: firstRule.rotationMode.id,
                name: firstRule.rotationMode.name,
              }
            : null,
          integrationAttribute: {
            id: firstRule.integrationAttribute.id,
            name: firstRule.integrationAttribute.name,
          },
          copyProcess: firstRule.copyProcess ? firstRule.copyProcess : null,
          renameFileProcess: firstRule.renameFileProcess
            ? firstRule.renameFileProcess
            : null,
          software: firstRule._4uBusAutomationExecution.software,
          uBusAutomationExecution: {
            id: firstRule._4uBusAutomationExecution.id,
            softwareId: firstRule._4uBusAutomationExecution.software.id,
            name: firstRule._4uBusAutomationExecution.name,
            code: firstRule._4uBusAutomationExecution.code,
          },
        };

        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <>
      <FormContainer<TFileIntegrationForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "file_integration")}
        schema={fileIntegrationSchema}
        subtitle={t(translationPath + "new_file_integration")}
        subtitleWatchField={"name"}
        marginBottom={5}
        customHeight={"fit-content"}
        defaultValues={{
          medicalCertificateContent:
            "Este procedimento pelas suas características clínicas não tem a necessidade de um Laudo médico descrito, sendo que as imagens geradas pelo exame já são suficientes para a análise diagnóstica. Você pode encontrar as imagens do exame vinculadas em anexo (Caso esteja visualizando do portal do cliente, basta clicar no ícone 'Imagens').",
        }}
        fields={() => (
          <Grid container spacing={2}>
            <BasicData />
          </Grid>
        )}
      />
    </>
  );
};

export default FileIntegration;
